import { useState } from 'react'
import { Button } from '../../components/button'
import { SearchInput } from '../../components/inputs/searchInput'
import ModalComponent from '../../components/modal'
import {
  useGetDiscoverCardOptionsQuery,
  type CardSourceOption,
  type FeedCardType,
} from '../../redux-toolkits/discover/discover-card-slice'

function contentName(option: CardSourceOption) {
  return option.name ?? option.title ?? 'Unavailable content'
}

function contentImage(option: CardSourceOption) {
  return option.images?.[0] ?? option.banner ?? option.bannerUrl
}

function ContentThumbnail({ option }: { option: CardSourceOption }) {
  const image = contentImage(option)
  if (image) {
    return (
      <img
        src={image}
        alt=""
        className="h-16 w-16 shrink-0 rounded-lg object-cover"
      />
    )
  }
  return (
    <span
      aria-hidden="true"
      className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-gray-bg text-lg font-medium text-sec-black"
    >
      {contentName(option).slice(0, 2).toUpperCase()}
    </span>
  )
}

export function DiscoverContentField({
  title,
  selection,
  placeholder,
  disabled = false,
  onOpen,
}: {
  title: string
  selection?: CardSourceOption
  placeholder: string
  disabled?: boolean
  onOpen: () => void
}) {
  return (
    <section className="space-y-3">
      <h4 className="font-medium">{title}</h4>
      <button
        type="button"
        disabled={disabled}
        onClick={onOpen}
        className="flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={`${selection ? 'Change' : 'Select'} ${title.toLowerCase()}`}
      >
        {selection && <ContentThumbnail option={selection} />}
        <span className="min-w-0 flex-1">
          <span className="block font-medium">
            {selection
              ? contentName(selection)
              : `Select ${title.toLowerCase()}`}
          </span>
          <span className="mt-1 block text-sm text-sec-black">
            {selection?.address ?? placeholder}
          </span>
        </span>
        <span className="shrink-0 text-sm font-medium text-primary">
          {selection ? 'Change' : 'Browse'}
        </span>
      </button>
    </section>
  )
}

export function DiscoverContentPicker({
  type,
  title,
  selection,
  options,
  loading = false,
  error = false,
  onRetry,
  onSelect,
  onClose,
}: {
  type: FeedCardType
  title: string
  selection?: CardSourceOption
  options?: CardSourceOption[]
  loading?: boolean
  error?: boolean
  onRetry?: () => void
  onSelect: (option: CardSourceOption) => void
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(selection)
  const {
    currentData: results = [],
    isFetching,
    isError,
    refetch,
  } = useGetDiscoverCardOptionsQuery(
    { type, search: search.trim() || undefined },
    { skip: options !== undefined },
  )
  const filteredOptions = options?.filter((option) =>
    `${contentName(option)} ${option.address ?? ''}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  )
  const choices = filteredOptions ?? results
  const fetching = options === undefined ? isFetching : loading
  const failed = options === undefined ? isError : error
  const retry = () => {
    if (options === undefined) {
      void refetch()
      return
    }
    onRetry?.()
  }

  return (
    <ModalComponent
      open
      setOpen={(open) => {
        if (!open) onClose()
      }}
      title={`Select ${title.toLowerCase()}`}
      childrenClassName="md:!max-w-[640px]"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Select ${title.toLowerCase()}`}
        className="flex flex-col gap-5"
      >
        <SearchInput
          placeholder={`Search ${title.toLowerCase()}`}
          searchFunction={setSearch}
          className="!max-w-full"
        />
        <div
          className="max-h-[45vh] min-h-[200px] overflow-y-auto"
          aria-busy={fetching}
        >
          {fetching && (
            <p role="status" className="py-10 text-center text-sec-black">
              Loading content…
            </p>
          )}
          {failed && (
            <p role="alert" className="py-8 text-center text-sec-black">
              Unable to load content.{' '}
              <button type="button" className="underline" onClick={retry}>
                Retry
              </button>
            </p>
          )}
          {!fetching && !failed && choices.length === 0 && (
            <p className="py-10 text-center text-sec-black">
              {search
                ? 'No matching content. Try another search.'
                : 'No content available to select.'}
            </p>
          )}
          {!fetching &&
            !failed &&
            choices.map((option) => (
              <label
                key={option._id}
                className={`mb-3 flex cursor-pointer items-center gap-4 rounded-xl border p-4 transition-colors hover:border-primary ${
                  selected?._id === option._id
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200'
                }`}
              >
                <ContentThumbnail option={option} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-black">
                    {contentName(option)}
                  </span>
                  {option.address && (
                    <span className="mt-1 block text-sm text-sec-black">
                      {option.address}
                    </span>
                  )}
                </span>
                <input
                  type="radio"
                  name="discover-content"
                  checked={selected?._id === option._id}
                  onChange={() => setSelected(option)}
                  className="h-4 w-4 shrink-0 accent-primary"
                />
              </label>
            ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 border-t pt-4">
          <p
            className="min-w-0 flex-1 text-sm text-sec-black"
            aria-live="polite"
          >
            {selected ? contentName(selected) : 'Choose one item to continue.'}
          </p>
          <div className="flex gap-3">
            <Button type="button" label="Cancel" onClick={onClose} />
            <Button
              type="button"
              primary
              label="Use selection"
              disabled={!selected || fetching || failed}
              onClick={() => {
                if (selected) onSelect(selected)
              }}
            />
          </div>
        </div>
      </div>
    </ModalComponent>
  )
}
